import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { dimensionMaturityLineOverallVersusBenchmarkRecords } from "../../../services/api-services";
import { toastEmitter } from "../../../utills/utilities";
import { API_RESPONSE } from "../../../utills/app-constant";
import generateChartImage from "./generateChartImage";
import Remark from "./Remark";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const MaturityLine = ({ selectedCompanyId, graphName }) => {
  const [loading, setLoading] = useState(false);
  const [dimensionsData, setDimensionsData] = useState(null);
  const [remark, setRemark] = useState("");
  const [isAttempt, setIsAttempt] = useState("")
  const [remarkId, setRemarkId] = useState(null);

  const fetchMaturityData = async () => {
    setLoading(true);
    try {
      const response = await dimensionMaturityLineOverallVersusBenchmarkRecords(selectedCompanyId, graphName);
      if (response?.data?.responseCode === 200) {
        setDimensionsData(response?.data?.responseBody);
        const remark = response?.data?.remark || "";
        const remarkId = response?.data?.remarkId || null;
        const isAttempt = response?.data?.isAttempt ?? 0;

        // Set remark and remarkId to state
        setRemark(remark);
        setRemarkId(remarkId);
        setIsAttempt(isAttempt)
      } else {
        toastEmitter("error", response?.data?.message);
      }
    } catch (err) {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCompanyId) {
      fetchMaturityData();
    }
  }, [selectedCompanyId]);

  const generateChartOptions = (benchmarkLineDimensionResponseList, overallData) => {
    const annotations = [
      // Overall data annotations
      {
        x: overallData.currentCompanyOverallCurrentAvg,
        y: "Overall",
        marker: {
          size: 12,
          fillColor: "#80c740",
          shape: "circle",
          strokeWidth: 0,
          offsetY: -4,
        },
        label: {
          text: `${overallData.currentCompanyOverallCurrentAvg}`,
          style: {
            fontSize: "10px",
            color: "#999999",
          },
        },
      },
      {
        x: overallData.previousCompaniesOverallCurrentAvg,
        y: "Overall",
        marker: {
          size: 12,
          fillColor: "#999999",
          shape: "star",
          strokeWidth: 0,
          offsetY: -4,
        },
        label: {
          text: `${overallData.previousCompaniesOverallCurrentAvg}`,
          style: {
            fontSize: "10px",
            color: "#5ccafd",
          },
        },
      },
      {
        x: overallData.currentCompanyOverallFutureAvg,
        y: "Overall",
        marker: {
          size: 12,
          fillColor: "#ffc000",
          shape: "circle",
          strokeWidth: 0,
          offsetY: -4,
        },
        label: {
          text: `${overallData.currentCompanyOverallFutureAvg}`,
          style: {
            fontSize: "10px",
            color: "#29cc27",
          },
        },
      },
      {
        x: overallData.previousCompaniesOverallFutureAvg,
        y: "Overall",
        marker: {
          size: 12,
          fillColor: "#5ccafd",
          shape: "star",
          strokeWidth: 0,
          offsetY: -4,
        },
        label: {
          text: `${overallData.previousCompaniesOverallFutureAvg}`,
          style: {
            fontSize: "10px",
            color: "#29cc27",
          },
        },
      },
    ];

    benchmarkLineDimensionResponseList.forEach((dimension) => {
      annotations.push(
        {
          x: dimension.currentCompanyCurrentAvg,
          y: dimension.dimensionName,
          marker: {
            size: 12,
            fillColor: "#80c740",
            shape: "circle",
            strokeWidth: 0,
            offsetY: -4,
          },
          label: {
            text: `${dimension.currentCompanyCurrentAvg}`,
            style: {
              fontSize: "10px",
              color: "#999999",
            },
          },
        },
        {
          x: dimension.previousCompaniesCurrentAvg,
          y: dimension.dimensionName,
          marker: {
            size: 13,
            fillColor: "#999999",
            shape: "star",
            strokeWidth: 0,
            offsetY: -4,
          },
          label: {
            text: `${dimension.previousCompaniesCurrentAvg}`,
            style: {
              fontSize: "10px",
              color: "#5ccafd",
            },
          },
        },
        {
          x: dimension.currentCompanyFutureAvg,
          y: dimension.dimensionName,
          marker: {
            size: 12,
            fillColor: "#ffc000",
            shape: "circle",
            strokeWidth: 0,
            offsetY: -4,
          },
          label: {
            text: `${dimension.currentCompanyFutureAvg}`,
            style: {
              fontSize: "10px",
              color: "#29cc27",
            },
          },
        },
        {
          x: dimension.previousCompaniesFutureAvg,
          y: dimension.dimensionName,
          marker: {
            size: 13,
            fillColor: "#5ccafd",
            shape: "star",
            strokeWidth: 0,
            offsetY: -4,
          },
          label: {
            text: `${dimension.previousCompaniesFutureAvg}`,
            style: {
              fontSize: "10px",
              color: "#29cc27",
            },
          },
        }
      );
    });

    return {
      chart: {
        offsetY: 0,
        height: 420,
        id: 'benchmark_records',
        type: "rangeBar",
        events: {
          mounted: () => {
            setTimeout(() => {
              generateChartImage('benchmark_records', 'benchmark_records', remark); // Call the updated function here
            }, 100); // Delay in milliseconds (e.g., 100ms or adjust as necessary)
          },
        },
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        pan: {
          enabled: false,
        },
      },
      colors: ["#EC7D31", "#36BDCB"],
      plotOptions: {
        bar: {
          horizontal: true,
          isDumbbell: true,
        },
      },
      annotations: {
        points: annotations,
      },
      legend: {
        show: false,
        series: true,
        position: "top",
        horizontalAlign: "center",
        markers: {
          width: 15,
          height: 15,
          radius: 6,
        },
      },
      grid: {
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
      xaxis: {
        min: 0,
        max: 10,
        tickAmount: 21,
        labels: {
          formatter: function (value) {
            return `${Number(value).toFixed(1)}`;
          },
        },
      },
    };
  };

  const generateChartSeries = (benchmarkLineDimensionResponseList, overallData) => {
    const overallSeries = [
      {
        x: "Overall",
        y: [overallData.currentCompanyOverallCurrentAvg, overallData.currentCompanyOverallFutureAvg],
        mid: overallData.previousCompaniesOverallCurrentAvg,
        high: overallData.previousCompaniesOverallFutureAvg,
      }
    ];

    const dimensionSeries = benchmarkLineDimensionResponseList.map((dimension) => ({
      x: dimension.dimensionName,
      y: [dimension.currentCompanyCurrentAvg, dimension.currentCompanyFutureAvg],
      mid: dimension.previousCompaniesCurrentAvg,
      high: dimension.previousCompaniesFutureAvg,
    }));

    return [
      { data: overallSeries },
      { data: dimensionSeries },
    ];
  };


  const exportToExcel = (data, fileName = "Benchmark Summary") => {
    if (!data) return;

    const heading = ["Benchmark Summary"];
    const overallRows = [
      [`Previous Companies - Current Avg`, data.previousCompaniesOverallCurrentAvg.toFixed(2)],
      [`Previous Companies - Future Avg`, data.previousCompaniesOverallFutureAvg.toFixed(2)],
      [`Current Company - Current Avg`, data.currentCompanyOverallCurrentAvg.toFixed(2)],
      [`Current Company - Future Avg`, data.currentCompanyOverallFutureAvg.toFixed(2)]
    ];

    const headers = [
      "Dimension Name",
      "Current Company - Current Avg",
      "Current Company - Future Avg",
      "Previous Companies - Current Avg",
      "Previous Companies - Future Avg"
    ];

    const dimensionRows = data?.benchmarkLineDimensionResponseList?.map(d => [
      d.dimensionName || "Unnamed",
      d.currentCompanyCurrentAvg?.toFixed(2) ?? "0.00",
      d.currentCompanyFutureAvg?.toFixed(2) ?? "0.00",
      d.previousCompaniesCurrentAvg?.toFixed(2) ?? "0.00",
      d.previousCompaniesFutureAvg?.toFixed(2) ?? "0.00"
    ]) || [];

    const worksheet = XLSX.utils.aoa_to_sheet([
      heading,
      [],
      ...overallRows,
      [],
      headers,
      ...dimensionRows
    ]);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Benchmark Summary");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array"
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });

    saveAs(blob, fileName);
  };


  return (
    <>
      <div className="text-end">
        <button className="px-3 py-2 mb-3 text-white bg-primary rounded border-0 ms-2" onClick={() => exportToExcel(dimensionsData)}>Download Excel</button>
      </div>
      <div style={{ overflow: "hidden" }}>
        {loading ? (
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        ) : dimensionsData && dimensionsData.benchmarkLineDimensionResponseList?.length > 0 ? (
          <div style={{ marginBottom: "40px" }}>
            <div className="d-flex justify-content-center" style={{ gap: "10px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span
                  style={{
                    width: "20px",
                    height: "20px",
                    backgroundColor: "#29cc27",
                    borderRadius: "50%",
                    display: "inline-block",
                  }}
                ></span>
                <span>Current Score</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span
                  style={{
                    width: "20px",
                    height: "20px",
                    backgroundColor: "#fdbf2d",
                    borderRadius: "50%",
                    display: "inline-block",
                  }}
                ></span>
                <span>Future Score</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span
                  style={{
                    width: "20px",
                    height: "20px",
                    display: "inline-block",
                    clipPath:
                      "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)",
                    backgroundColor: "#a8a8a5",
                  }}
                ></span>
                <span>Current Benchmark (Org Level)</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span
                  style={{
                    width: "20px",
                    height: "20px",
                    display: "inline-block",
                    clipPath:
                      "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)",
                    backgroundColor: "#5ccafd",
                  }}
                ></span>
                <span>Future Benchmark (Org Level)</span>
              </div>
            </div>
            <div className="chart8" data-heading="Maturity Line – Overall versus benchmark records" data-remark={remark}>
              <Chart
                series={generateChartSeries(
                  dimensionsData.benchmarkLineDimensionResponseList,
                  {
                    currentCompanyOverallCurrentAvg: dimensionsData.currentCompanyOverallCurrentAvg,
                    currentCompanyOverallFutureAvg: dimensionsData.currentCompanyOverallFutureAvg,
                    previousCompaniesOverallCurrentAvg: dimensionsData.previousCompaniesOverallCurrentAvg,
                    previousCompaniesOverallFutureAvg: dimensionsData.previousCompaniesOverallFutureAvg,
                  }
                )}
                options={generateChartOptions(
                  dimensionsData.benchmarkLineDimensionResponseList,
                  {
                    currentCompanyOverallCurrentAvg: dimensionsData.currentCompanyOverallCurrentAvg,
                    currentCompanyOverallFutureAvg: dimensionsData.currentCompanyOverallFutureAvg,
                    previousCompaniesOverallCurrentAvg: dimensionsData.previousCompaniesOverallCurrentAvg,
                    previousCompaniesOverallFutureAvg: dimensionsData.previousCompaniesOverallFutureAvg,
                  }
                )}
                type="rangeBar"
                height={420}
              />
            </div>
          </div>
        ) : (
          <div>No data available</div>
        )}
        <Remark graphName="MaturityLineOverallversusbenchmark" selectedCompanyId={selectedCompanyId} initialRemark={remark}
          remarkId={remarkId} isAttempt={isAttempt} setRemarkprops={setRemark} />
      </div>
    </>

  );
};

export default MaturityLine;
